import { ksAppList } from './const';
import { getCookie } from '@/util/cookie';
import type { UrlMapFn, UrlMap, UrlMapFnParam } from '../config/types';
import { error } from './console';

/**
 * 获取 localStorage 缓存
 */
export function getLocalStorageItem(key: string) {
    try {
        if (window && window.localStorage) {
            const value = window.localStorage.getItem(key);
            if (value) {
                try {
                    return JSON.parse(value);
                } catch (err) {
                    return value;
                }
            }
        }
    } catch (err) {
        return null;
    }
    return null;

}
/**
 * 设置 localStorage 缓存
 */
export function setLocalStorageItem(key: string, value: any) {
    try {
        if (window && window.localStorage) {
            window.localStorage.setItem(key, JSON.stringify(value));
            return true;
        }
    } catch (err) {
        return false;
    }
    return false;
}

/**
 * 判断item是否在目标数组arr中
 * @param {*} arr   目标数组
 * @param {*} item  item
 */
export function inArray(arr: any[], item: any) {
    return arr.indexOf(item) > -1;
}
/**
 * 从目标arr中删除item
 * @param {*} arr  目标数组
 * @param {*} item  item
 */
export function deleteArray(arr: any[], item: any) {
    const index = arr.indexOf(item);

    if (index > -1) {
        arr.splice(index, 1);
    }
}

/**
 * 事件监听兼容处理
 * @param {*} evtTarget  事件监听目标
 * @param {*} evtName   事件名
 * @param {*} callback  回调函数
 * @param {*} options  相关参数设置
 */
export function addMonitor(
    evtTarget: Element | Window | Document,
    evtName: string,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
) {
    if ('attachEvent' in evtTarget) {
        return evtTarget.attachEvent('on' + evtName, callback);
    }
    return evtTarget.addEventListener(evtName, callback, options);
}

/**
 * 事件监听解绑兼容处理
 * @param {*} evtTarget  事件监听目标
 * @param {*} evtName   事件名
 * @param {*} callback  事件回调
 */
export function removeMonitor(
    evtTarget: Element | Window | Document,
    evtName: string,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
) {
    if ('attachEvent' in evtTarget) {
        return evtTarget.detachEvent('on' + evtName, callback);
    }
    return evtTarget.removeEventListener(evtName, callback, options);
}

export function leftPad(str: string, len: number, ch = ' ') {
    len = len - str.length;
    if (len <= 0) {
        return str;
    }
    let pad = '';
    while (len) {
        if (len & 1) {
            pad += ch;
        }
        len >>= 1;
        ch += ch;
    }
    return pad + str;
}

/**
 * 是否是快手系APP
 */
export const isInApp = (ua = window.navigator.userAgent.toLowerCase()) => {
    try {
        for (let i = 0; i < ksAppList.length; i++) {
            if (ua.indexOf(ksAppList[i]!.toLowerCase()) > -1) {
                return true;
            }
        }
    } catch (err) {
        return false;
    }
};

export function parseQueryString(queryString: string) {
    const result: any = {};
    const queries = queryString.split('&');
    for (const query of queries) {
        const [key, value] = query.split('=');
        if (!(key! in result)) {
            result[key!] = value;
            continue;
        }
        if (result[key!] instanceof Array) {
            result[key!].push(value);
            continue;
        }
        result[key!] = [result[key!], value];
    }
    return result;
}

export function stringifyQuery(paramsString: any) {
    const params = typeof paramsString === 'string' ? JSON.parse(paramsString) : paramsString;
    const queries: string[] = [];
    Object.keys(params).forEach(key => {
        if (params[key] instanceof Array) {
            for (const val of params[key]) {
                queries.push(`${key}=${val}`);
            }
        } else if (typeof params[key] === 'object' && params[key] !== null) {
            queries.push(`${key}=${JSON.stringify(params[key])}`);
        } else {
            queries.push(`${key}=${params[key]}`);
        }
    });
    return queries.join('&');
}

// 参考：https://codereview.stackexchange.com/questions/37512/count-byte-length-of-string
export function getStringBytes(data: any) {
    let dataStr = '';

    // Force string type
    if (typeof data === 'string') {
        dataStr = data;
    } else {
        try {
            dataStr = JSON.stringify(data);
        } catch (e) {
            dataStr = String(data);
        }
    }

    let byteLen = 0;
    for (let i = 0; i < dataStr.length; i++) {
        const c = dataStr.charCodeAt(i);
        byteLen += c < (1 << 7) ? 1 :
            c < (1 << 11) ? 2 :
                c < (1 << 16) ? 3 :
                    c < (1 << 21) ? 4 :
                        c < (1 << 26) ? 5 :
                            c < (1 << 31) ? 6 : Number.NaN;
    }
    return byteLen;
}

/**
 * lodash 的 pick 方法
 */
export function pick(data: any, paths: string[]) {
    let index = -1;
    const length = paths.length;
    const result: any = {};

    while (++index < length) {
        const path = paths[index];
        const value = data[path!];
        result[path!] = value;
    }

    return result;
}

/**
 * lodash的omit方法
 * @param data
 * @param paths
 */
export function omit(data: any, paths: string[]) {
    const result: any = {};

    for (const i in data) {
        if (data.hasOwnProperty(i)) {
            if (Array.isArray(paths) && paths.indexOf(i) === -1) {
                result[i] = data[i];
            }
        }
    }

    return result;
}

/**
 * 检查依赖版本号是否不小于推荐版本
 */
export function checkVersion(recommendVersion: string, version: string) {
    if (!version) return false;
    if (version.indexOf('alpha') !== -1) return false;
    if (version.indexOf('beta') !== -1) return false;
    try {
        let matches = recommendVersion.match(/\d+\.\d+\.\d+/);
        recommendVersion = matches?.[0] || '';
        if (!recommendVersion) return true;

        matches = version.match(/\d+\.\d+\.\d+/);
        version = matches?.[0] || '';
        if (!version) return false;

        const [rMajor, rMinor, rPatch] = recommendVersion.split('.').map((i: string) => Number(i));
        const [major, minor, patch] = version.split('.').map((i: string) => Number(i));

        if (major! < rMajor!) return false;
        if (major! > rMajor!) return true;

        if (minor! < rMinor!) return false;
        if (minor! > rMinor!) return true;

        if (patch! < rPatch!) return false;

        return true;
    } catch (err) {
        return false;
    }
}

export function checkSupportCloseCoPage() {
    const version = getCookie('appver', undefined, true);
    return version && checkVersion('10.8.30', version);
}
/**
 * 获取元素xpath
 * @param element 元素
 */
export function getXpathFromNode(element: HTMLElement): string {
    // if (element && element.id !== '')
    //     return 'id("' + element.id + '")';
    if (element === document.body)
        return 'HTML/' + element.tagName;

    let ix = 0;
    if (element && element.parentNode) {
        const siblings = element.parentNode.childNodes;
        for (let i = 0; i < siblings.length; i++) {
            const sibling = siblings[i] as HTMLElement;
            if (sibling === element)
                return getXpathFromNode(element.parentNode as HTMLElement) + '/' + element.tagName + '[' + (ix + 1) + ']';
            if (sibling.nodeType === 1 && sibling.tagName === element.tagName)
                ix++;
        }
    }
    return '';
}

/**
 * url中获取path和query
 */
export function getPathAndQueryFromUrl() {
    const { protocol, host, pathname, search, href } = window.location;
    const pageUrl = href;
    const pagePath = protocol + host + pathname;
    const pageParams = parseQueryString(search.slice(1));
    return { pageUrl, pagePath, pageParams };
}

export function getUrlAndQueryFromUrl(url = location.href) {
    const queryIndex = url.lastIndexOf('?');
    if (queryIndex === -1) {
        return {
            page: url
        };
    }
    const page = url.slice(0, queryIndex);
    const queryString = url.slice(queryIndex + 1);
    return {
        page,
        params: parseQueryString(queryString),
    };
};

export const getMappingPage = (urlPackage: UrlMapFnParam, urlMap?: UrlMap | UrlMapFn) => {
    let { url, page, params, pageId } = urlPackage;
    if (!page && typeof url === 'string') {
        const parsed = getUrlAndQueryFromUrl(url);
        page = parsed.page;
        params = parsed.params;
    }
    if (typeof urlMap === 'function') {
        try {
            const res = urlMap({ url, page, params });
            if (typeof res === 'string') {
                page = res;
            } else if (typeof res === 'object') {
                if (res.page) {
                    page = res.page;
                }
                if (res.params) {
                    params = res.params;
                }
                if (res.pageId) {
                    pageId = res.pageId;
                }
            }
        } catch (err) { }
    } else if (typeof urlMap === 'object') {
        let mappingPage = '';
        for (const key in urlMap) {
            if ((url || page || '').indexOf(key) > -1) {
                mappingPage = urlMap[key]!;
                break;
            }
        }
        if (mappingPage) {
            page = mappingPage;
        }
    }
    return {
        page,
        params,
        pageId
    };
};

export function cloneDeep(obj: [] | {}): [] | {} {
    if (typeof obj !== 'object') return {};
    const newObj = obj instanceof Array ? [] : {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            // @ts-ignore
            newObj[key] = typeof obj[key] === 'object' ? cloneDeep(obj[key]) : obj[key];
        }
    }
    return newObj;
}

/**
 * 非空纯对象判定
 * @param obj
 * @returns
 */
 const isObj = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj);
 /**
  * 对象打平赋值
  * @param target
  * @param options
  * @returns
  */
 export const flattenAssign = (target: any, options: any) => {
    if (!isObj(target) || !isObj(options)) return;
    const merge = (obj1: any, obj2: any, key: string) => {
        if (isObj(obj1[key]) && isObj(obj2[key])) {
            Object.assign(obj1[key], obj2[key]);
        } else if (!isObj(obj1[key]) && !isObj(obj2[key])){
            obj1[key] = obj2[key];
        }
    }
    for (const key in options) {
        if (target.hasOwnProperty(key)) {
            merge(target, options, key);
            continue;
        }
        for (const deepKey in target) {
            if (isObj(target[deepKey]) && target[deepKey].hasOwnProperty(key)) {
                merge(target[deepKey], options, key);
            }
        }
    }
}

export function pageUrlValidate(page: string) {
    // 对于上报到v2，page为url的埋点，给予警告
    if (page && /^(https?:)?\/\//.test(page)) {
        error('[error 108]',`请注意当前埋点页面信息为 ${page}，不符合规范，上报失败！`);
        return false;
    }
    return true;
}

export const getResolution = (() => {
    let resolution = '';
    return () => {
        if (resolution) {
            return resolution;
        }
        try {
            const ratio = window.devicePixelRatio || 1;
            const width = Math.floor(screen.width * ratio); // 有的devicePixelRatio为小数，在此转为整数
            const height = Math.floor(screen.height * ratio);
            resolution = `${width}x${height}`;
            return resolution;
        } catch (err) {
            return '';
        }
    };
})();

// 从vue-router里面拿的……
export function supportsPushState() {
    const ua = window.navigator.userAgent;
    if (
        (ua.indexOf('Android 2.') !== -1 || ua.indexOf('Android 4.0') !== -1)
        && ua.indexOf('Mobile Safari') !== -1
        && ua.indexOf('Chrome') === -1
        && ua.indexOf('Windows Phone') === -1
    ) {
        return false;
    }
    return window.history && 'pushState' in window.history;
}

export function deleteUrlQueryAndReturnFullUrl(url: string, deleteKeyArray: string[]) {
    const { page, params } = getUrlAndQueryFromUrl(url);
    if (Array.isArray(deleteKeyArray) && deleteKeyArray.length) {
        deleteKeyArray.forEach(key => {
            delete params[key];
        });
        let paramsString = '';
        for (const i in params) {
            if (params.hasOwnProperty(i)) {
                paramsString += paramsString ? '&' : '';
                paramsString += `${i}=${params[i]}`;
            }
        }
        return paramsString ? `${page}?${paramsString}` : page;
    }
    return url;
}

interface TypeQueryMap {
    [key: string]: string | number;
}

/**
 * 设置url中的参数，并返回完整url
 * @param url
 * @param configQueryMap
 */
export function setUrlQueryAndReturnFullUrl(url: string, configQueryMap: TypeQueryMap): string {
    if (typeof configQueryMap !== 'object') {
        return url;
    }
    let paramsString = '';
    for (const i in configQueryMap) {
        if (configQueryMap.hasOwnProperty(i)) {
            const paramString = `${i}=${configQueryMap[i]}`;
            paramsString += paramsString ? `&${paramString}` : paramString;
        }
    }
    let partUrl;
    const lastChar = url[url.length - 1];
    if (url.indexOf('?') === -1) {
        // 不存在变量
        partUrl = `?${paramsString}`;
    } else if (lastChar === '?' || lastChar === '&') {
        // 处理最后一个字符是 ? 以及 & 的特殊情况
        partUrl = paramsString;
    } else {
        // 正常添加变量
        partUrl = `&${paramsString}`;
    }
    return url + partUrl;
}

export function isMobileAndIpad() {
    const ua = window?.navigator?.userAgent || '';
    const isMobile = /mobile|tablet|ip(ad|hone|od)|android|(windows phone)/i.test(ua);
    // 兼容最新版本 ipad 版本 ua 判断
    const isiPad = window?.navigator.platform === 'MacIntel' && window?.navigator?.maxTouchPoints > 1;
    return isMobile || isiPad;
}

/**
 * 是否支持 worker gzip 压缩
 * @returns
 */
export const enableAsyncGzip = () => !!(
    window?.Worker // 可以新建 worker
    && window?.Uint8Array // 支持二进制存储
    && window.URL // 可以创建文件路径
    && window.Promise // 可以使用 promise
    && !isMobileAndIpad() // 移动端禁止使用压缩
);

/**
 * 返回正整数数值
 * @param value
 */
export const getPositiveInteger = (value: number) => {
    try {
        return Math.abs(Math.floor(value));
    } catch (e) {
        return value;
    }
};

export function stringifyParams(params: string | any) {
    return typeof params === 'string' ? params : typeof params === 'object' ? JSON.stringify(params) : '';
}
